/**
 * A person wrote while the visitor wasn't looking (ADR-0097): the count, the
 * page's title and a soft sound. The launcher shows the count and the
 * message; this only keeps the state.
 *
 * - The title gets "(1) " in front and goes back exactly as it was; if the
 *   page changed its own title meanwhile, the page's wins.
 * - The sound plays only after the visitor used the widget (`arm`, from a
 *   click or a key): browsers block audio before that, and a page that was
 *   only opened should never make noise. Never while `muted()` (the
 *   visitor's own switch, ADR-0098).
 * - Safari suspends the audio of a page out of view: every gesture and every
 *   chime asks for it back (ADR-0098).
 */

export function unreadTracker({ doc = document, win = window, onChange = () => {}, muted = () => false } = {}) {
  let count = 0;
  let pageTitle = null;
  let ourTitle = null;
  let audio = null;

  const showTitle = () => {
    if (count === 0) {
      if (ourTitle !== null && doc.title === ourTitle) doc.title = pageTitle;
      pageTitle = null;
      ourTitle = null;
      return;
    }
    // First time, or the page set a title of its own since.
    if (ourTitle === null || doc.title !== ourTitle) pageTitle = doc.title;
    ourTitle = `(${count}) ${pageTitle}`;
    doc.title = ourTitle;
  };

  const wake = () => {
    if (audio && audio.state !== 'running') {
      try { audio.resume?.()?.catch?.(() => {}); } catch { /* stays silent */ }
    }
  };

  const chime = () => {
    if (!audio || muted()) return;
    wake();
    try {
      const start = audio.currentTime;
      const gain = audio.createGain();
      gain.connect(audio.destination);
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.06, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.4);
      // Two short notes, rising: noticeable, never an alarm.
      for (const [frequency, at] of [[660, 0], [880, 0.12]]) {
        const tone = audio.createOscillator();
        tone.type = 'sine';
        tone.frequency.setValueAtTime(frequency, start + at);
        tone.connect(gain);
        tone.start(start + at);
        tone.stop(start + at + 0.25);
      }
    } catch { /* no sound is fine */ }
  };

  return {
    /** Called from the visitor's own click or key in the widget. */
    arm() {
      if (audio) {
        wake();
        return;
      }
      const Context = win.AudioContext || win.webkitAudioContext;
      if (!Context) return;
      try {
        audio = new Context();
        audio.resume?.();
      } catch {
        audio = null;
      }
    },
    add(message) {
      count += 1;
      showTitle();
      chime();
      onChange(count, message);
    },
    clear() {
      if (count === 0) return;
      count = 0;
      showTitle();
      onChange(0, null);
    },
    get count() {
      return count;
    },
  };
}
